import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { StepContext } from '../../../creator/step.context';
import { FORM_FIELDS, FORM_STEPS } from '../../../../../constants';
import { validateFields } from '../../../../../helpers/form.new';
import { getValidationSchema } from '../../../common.validators';
import { format } from '../../../../../helpers';
import { needed as fieldNeeded } from '../../../steps/work/dependencies';

export const useStepSubtitle = (stepIndex: number): string | null => {
    const { currentStep, stepsConfig } = useContext(StepContext);
    const form = useFormContext();
    const [subTitle, setSubTitle] = useState<string | null>(null);
    const prevValues = useRef('');

    const {
        watch,
        formState: { dirtyFields: _ },
    } = form;

    const stepSubtitleDeps = useMemo(
        () => ({
            [FORM_STEPS.PARAMS]: [FORM_FIELDS.AMOUNT, FORM_FIELDS.REGION],
            [FORM_STEPS.PASSPORT]: [FORM_FIELDS.PASSPORT_NUMBER, FORM_FIELDS.PASSPORT_DATE, FORM_FIELDS.BIRTHDAY],
            [FORM_STEPS.PERSONAL_DATA]: [FORM_FIELDS.EMAIL],
            [FORM_STEPS.WORK]: [
                FORM_FIELDS.EMPLOYMENT_TYPE,
                FORM_FIELDS.ORGANIZATION_NAME,
                FORM_FIELDS.LAST_EXPERIENCE_START_MONTH,
                FORM_FIELDS.LAST_EXPERIENCE_START_YEAR,
            ],
        }),
        [],
    );

    const fields = useMemo(() => stepSubtitleDeps[stepsConfig[stepIndex].stepName] || [], [stepIndex]) as Array<string>;

    const values = watch(fields);

    const valuesString = useMemo(() => values.join(' '), [values]);
    const makeFieldValueObj = (fields: Array<string>, values: Array<any>) => {
        return fields.reduce(
            (prev, current, currentIndex) => {
                return { ...prev, [current]: values[currentIndex] };
            },
            {} as Record<string, any>,
        );
    };

    const generateSubtitle = async (fieldsValues: Record<string, any>) => {
        const isValid = await validateFields(getValidationSchema(fields), fieldsValues);
        if (!isValid) {
            setSubTitle(null);
            return;
        }
        switch (stepsConfig[stepIndex].stepName) {
            case FORM_STEPS.PARAMS: {
                setSubTitle([format(fieldsValues[FORM_FIELDS.AMOUNT]), fieldsValues[FORM_FIELDS.REGION]].join(', '));
                break;
            }
            case FORM_STEPS.PASSPORT: {
                setSubTitle(
                    `${fieldsValues[FORM_FIELDS.PASSPORT_NUMBER]} от ${fieldsValues[FORM_FIELDS.PASSPORT_DATE]}`,
                );
                break;
            }
            case FORM_STEPS.PERSONAL_DATA: {
                setSubTitle(fieldsValues[FORM_FIELDS.EMAIL]);
                break;
            }
            case FORM_STEPS.WORK: {
                if (fieldNeeded(FORM_FIELDS.ORGANIZATION_NAME, fieldsValues[FORM_FIELDS.EMPLOYMENT_TYPE])) {
                    setSubTitle(
                        `${fieldsValues[FORM_FIELDS.ORGANIZATION_NAME]} от ${
                            fieldsValues[FORM_FIELDS.LAST_EXPERIENCE_START_MONTH]
                        }.${fieldsValues[FORM_FIELDS.LAST_EXPERIENCE_START_YEAR]}`,
                    );
                } else {
                    setSubTitle(fieldsValues[FORM_FIELDS.EMPLOYMENT_TYPE]);
                }
                break;
            }
            default: {
                break;
            }
        }
    };

    useEffect(() => {
        if (stepIndex === currentStep || values.includes(undefined) || prevValues.current === valuesString) {
            return;
        }
        const fieldsValues = makeFieldValueObj(fields, values);
        generateSubtitle(fieldsValues);
        prevValues.current = valuesString;
    }, [currentStep, valuesString]);

    return subTitle;
};
